import { IntegrationProvider } from "@repo/app-types/entities/integration";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { useState, type FC } from "react";
import { useDisconnectZoomContactCenterIntegrationMutation } from "../../../api/entities/zoomContactCenterIntegration.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { integrationConfigs } from "./integrationConfigs.js";
import { useZoomContactCenterDetailOutletContext } from "./zoomContactCenterIntegrationDetailSection.js";
import { ZoomContactCenterIntegrationSettings } from "./zoomContactCenterIntegrationSettings.js";

const zoomConfig = integrationConfigs.find(
  (config) => config.provider === IntegrationProvider.ZOOM_CONTACT_CENTER,
);

/**
 * The Zoom CC detail page's **Settings** tab: the existing connection UI
 * (connect / status / rotate / pause / disconnect), plus the disconnect
 * confirmation it drives. Reads the shared integration off the detail section's
 * `Outlet` context.
 */
export const ZoomContactCenterSettingsTab: FC = () => {
  const { integration, isLoading, isError, onReload } =
    useZoomContactCenterDetailOutletContext();
  const [disconnectZoomContactCenterIntegration] =
    useDisconnectZoomContactCenterIntegrationMutation();
  const [isDisconnectOpen, setIsDisconnectOpen] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  const handleDisconnect = async () => {
    if (!integration) {
      return;
    }
    setDisconnectError(null);
    try {
      await disconnectZoomContactCenterIntegration({
        integrationId: integration.id,
      }).unwrap();
    } catch (error) {
      setDisconnectError(
        getApiErrorMessage(
          error,
          "Could not disconnect Zoom Contact Center — please try again.",
        ),
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* The logo sits next to the name in the page header; the tab body leads
          with just the description, matching the other integration pages. */}
      <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
        {zoomConfig?.description}
      </p>

      {/* A refused disconnect is the customer's to act on, so it is said out
          here rather than swallowed with the modal. */}
      {disconnectError ? (
        <p className="text-sm text-red-600">{disconnectError}</p>
      ) : null}

      <ZoomContactCenterIntegrationSettings
        integration={integration}
        isLoading={isLoading}
        isError={isError}
        onReload={onReload}
        onRequestDisconnect={() => setIsDisconnectOpen(true)}
      />

      <SimpleModal
        isOpen={isDisconnectOpen}
        onCancel={() => setIsDisconnectOpen(false)}
        variant={SimpleModalVariant.Warning}
        title={`Disconnect ${zoomConfig?.name ?? "Zoom Contact Center"}?`}
        subtitle={zoomConfig?.disconnectWarning}
        confirmButtonText="Disconnect"
        onConfirm={() => {
          void handleDisconnect();
          setIsDisconnectOpen(false);
        }}
      />
    </div>
  );
};
